import React, { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import Cropper from "react-easy-crop";
import { Area } from "react-easy-crop/types";
import { useUploadImage } from "../../hooks/useImages";

interface ImageCropperModalProps {
  open: boolean;
  onClose: () => void;
  imageUrl: string;
  imageTitle?: string;
  aspectRatio?: number;
  allowResize?: boolean;
  onCropComplete: (croppedImageUrl: string) => void;
}

export const ImageCropperModal: React.FC<ImageCropperModalProps> = ({
  open,
  onClose,
  imageUrl,
  imageTitle = "Image",
  aspectRatio,
  allowResize = true,
  onCropComplete,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [useAspectRatio, setUseAspectRatio] = useState(aspectRatio !== undefined);
  const uploadImage = useUploadImage();

  const onCropChange = useCallback((crop: { x: number; y: number }) => {
    setCrop(crop);
  }, []);

  const onZoomChange = useCallback((zoom: number) => {
    setZoom(zoom);
  }, []);

  const onCropCompleteCallback = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", (error) => reject(error));
      image.setAttribute("crossOrigin", "anonymous");
      image.src = url;
    });

  const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: Area
  ): Promise<Blob> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("No 2d context");
    }

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Failed to create blob"));
          }
        },
        "image/jpeg",
        0.95
      );
    });
  };

  const handleSave = async () => {
    if (!croppedAreaPixels) {
      return;
    }

    setIsUploading(true);
    try {
      const croppedBlob = await getCroppedImg(imageUrl, croppedAreaPixels);
      const croppedFile = new File(
        [croppedBlob],
        `${imageTitle}-cropped.jpg`,
        { type: "image/jpeg" }
      );

      const timestamp = Date.now();
      const publicId = `${imageTitle.toLowerCase().replace(/\s+/g, "-")}-${timestamp}`;

      const result = await uploadImage.mutateAsync({
        file: croppedFile,
        public_id: publicId,
        folder: "mariah-photography/cropped",
        title: `${imageTitle} (Cropped)`,
        alt_text: `Cropped ${imageTitle}`,
      });

      // Get the Cloudinary URL from the result
      // The result is ImageUploadResponse which has data.url field
      const croppedImageUrl = result.data?.url;
      if (croppedImageUrl) {
        onCropComplete(croppedImageUrl);
        handleClose();
      } else {
        throw new Error("Failed to get cropped image URL");
      }
    } catch (error) {
      console.error("Failed to crop and upload image:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setUseAspectRatio(aspectRatio !== undefined);
      onClose();
    }
  };

  // Reset aspect ratio toggle when modal opens
  useEffect(() => {
    if (open) {
      setUseAspectRatio(aspectRatio !== undefined);
    }
  }, [open, aspectRatio]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ px: 4, pt: 4, pb: 2 }}>Crop Image</DialogTitle>
      <DialogContent
        sx={{
          px: 0,
          py: 2,
          position: "relative",
          height: { xs: "400px", sm: "500px" },
          backgroundColor: "#000",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "100%",
          }}
        >
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            aspect={useAspectRatio && aspectRatio ? aspectRatio : undefined}
            onCropChange={onCropChange}
            onZoomChange={onZoomChange}
            onCropComplete={onCropCompleteCallback}
            cropShape="rect"
            showGrid={false}
          />
        </Box>
      </DialogContent>
      <DialogContent sx={{ px: 4, py: 2 }}>
        {allowResize && aspectRatio && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <input
                type="checkbox"
                id="lock-aspect"
                checked={useAspectRatio}
                onChange={(e) => setUseAspectRatio(e.target.checked)}
                style={{ cursor: "pointer" }}
              />
              <Typography
                component="label"
                htmlFor="lock-aspect"
                variant="body2"
                sx={{ cursor: "pointer" }}
              >
                Lock aspect ratio ({aspectRatio.toFixed(2)}:1)
              </Typography>
            </Box>
          </Box>
        )}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Zoom
          </Typography>
          <input
            type="range"
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            onChange={(e) => onZoomChange(Number(e.target.value))}
            style={{ width: "100%" }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ gap: 3, px: 4, py: 3 }}>
        <Button
          onClick={handleClose}
          disabled={isUploading}
          disableRipple
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isUploading || !croppedAreaPixels}
        >
          {isUploading ? <CircularProgress size={20} /> : "Save Cropped Image"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
